import { useState } from 'react';
import api from '../../API';
import styles from './CreateFolderModal.module.css';

const FOLDER_TYPES = [
    {
        value: 'organization',
        label: 'Folder',
        description: 'A folder for grouping projects and Slide Sets.',
    },
    {
        value: 'project',
        label: 'Project',
        description: 'An annotation project with participants and a task UI.',
    },
    {
        value: 'slideset',
        label: 'Slide Set',
        description: 'A read-only slide collection with optional public sharing and item metadata.',
    },
];

const PROJECT_SUBFOLDER_TYPES = [
    {
        value: 'project-subfolder',
        label: 'Subfolder',
        description: 'A subfolder within the project for grouping items.',
    },
];

const SLIDESET_SUBFOLDER_TYPES = [
    {
        value: 'slideset-subfolder',
        label: 'Subfolder',
        description: 'A subfolder within the Slide Set for grouping items.',
    },
];

/**
 * Modal for creating a new folder within the GirderProjects tree.
 *
 * Props:
 *   isOpen        — boolean
 *   onClose       — () => void
 *   parentId      — string (ID of the parent collection or folder)
 *   parentType    — 'collection' | 'folder'
 *   parentFolderType — girderProjects.type of the parent folder, or null for collection root
 *   onCreated     — (newFolder) => void  called on success
 *   mode          — 'project' | 'slideset' | 'folder' | 'subfolder' | 'auto' (default 'auto')
 */
function CreateFolderModal({
    isOpen,
    onClose,
    parentId,
    parentType,
    parentFolderType,
    onCreated,
    mode = 'auto',
}) {
    const [name, setName] = useState('');
    const [folderType, setFolderType] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const isProjectContext = parentFolderType === 'project' || parentFolderType === 'project-subfolder';
    const isSlideSetContext = parentFolderType === 'slideset' || parentFolderType === 'slideset-subfolder';

    let fixedFolderType = null;
    let title;
    let submitLabel;

    if (mode === 'project') {
        fixedFolderType = 'project';
        title = 'New project';
        submitLabel = submitting ? 'Creating project…' : 'Create project';
    } else if (mode === 'slideset') {
        fixedFolderType = 'slideset';
        title = 'New Slide Set';
        submitLabel = submitting ? 'Creating Slide Set…' : 'Create Slide Set';
    } else if (mode === 'folder') {
        fixedFolderType = isProjectContext
            ? 'project-subfolder'
            : (isSlideSetContext ? 'slideset-subfolder' : 'organization');
        title = (isProjectContext || isSlideSetContext) ? 'New subfolder' : 'New folder';
        submitLabel = submitting ? 'Creating folder…' : 'Create folder';
    } else if (mode === 'subfolder') {
        fixedFolderType = isSlideSetContext ? 'slideset-subfolder' : 'project-subfolder';
        title = 'New subfolder';
        submitLabel = submitting ? 'Creating subfolder…' : 'Create subfolder';
    } else {
        if (isProjectContext || isSlideSetContext) {
            title = 'New subfolder';
        } else {
            title = 'New folder';
        }
        submitLabel = submitting ? 'Creating…' : 'Create';
    }

    const typeOptions = fixedFolderType
        ? []
        : (isProjectContext
            ? PROJECT_SUBFOLDER_TYPES
            : (isSlideSetContext ? SLIDESET_SUBFOLDER_TYPES : FOLDER_TYPES));

    const effectiveFolderType = fixedFolderType
        || folderType
        || (typeOptions.length === 1 ? typeOptions[0].value : folderType);

    function handleClose() {
        setName('');
        setFolderType('');
        setError(null);
        onClose();
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Please enter a folder name.');
            return;
        }
        if (!effectiveFolderType) {
            setError('Please select a folder type.');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const response = await api.post('projects/folders', {
                query: {
                    parentId,
                    parentType,
                    name: trimmedName,
                    folderType: effectiveFolderType,
                },
            });
            if (response.status === 200) {
                onCreated?.(response.data);
                handleClose();
            } else {
                setError(response.data?.message || 'Failed to create folder.');
            }
        } catch (err) {
            setError(err.message || 'Failed to create folder.');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className={styles.modalOverlay} onClick={handleClose} role="presentation">
            <div
                className={styles.modalContent}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="create-folder-title"
            >
                <div className={styles.modalHeader}>
                    <h2 id="create-folder-title" className={styles.modalTitle}>{title}</h2>
                    <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Close">
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className={styles.modalBody}>
                        {error && <div className={styles.errorMessage}>{error}</div>}
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel} htmlFor="create-folder-name">Name</label>
                            <input
                                id="create-folder-name"
                                className={styles.textInput}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoFocus
                                required
                            />
                        </div>
                        {typeOptions.length > 0 && (
                            <div className={styles.fieldGroup}>
                                <span className={styles.fieldLabel}>Type</span>
                                <div className={styles.typeOptions}>
                                    {typeOptions.map((opt) => (
                                        <label key={opt.value} className={styles.typeOption}>
                                            <input
                                                type="radio"
                                                name="folderType"
                                                value={opt.value}
                                                checked={folderType === opt.value}
                                                onChange={() => setFolderType(opt.value)}
                                            />
                                            <span>
                                                <strong>{opt.label}</strong>
                                                <span className={styles.typeDescription}>{opt.description}</span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.cancelButton} onClick={handleClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={submitting}>
                            {submitLabel}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateFolderModal;
